import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { firstValueFrom } from 'rxjs';
import { PatientDailyNotesActions } from 'app/view/journal/state/patient-daily-notes.actions';
import { CurrentPatientDailyNotesApiService } from 'app/view/journal/services/current-patient-daily-notes-api.service';
import { PatientNoteDTO } from 'app/view/journal/data/PatientNoteDTO';
import { PatientNoteCalendarDayDTO } from 'app/view/journal/data/PatientNoteDalendarDayDTO';
import { isSameDay } from 'date-fns';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationModalComponent } from '@shared/component/modal/confirmation/confirmation-modal.component';

export interface PatientDailyNotesStateModel {
  currentNote: PatientNoteDTO;
  dailyNotesForMonth: PatientNoteDTO[];
  selectedNote: PatientNoteDTO;
  calendarDays: PatientNoteCalendarDayDTO;
}

@State<PatientDailyNotesStateModel>({
  name: 'patientDailyNotes'
})
@Injectable()
export class PatientDailyNotesState {
  constructor(private patientDailyNotesApiService: CurrentPatientDailyNotesApiService,
              private modalService: BsModalService) {
  }

  @Action(PatientDailyNotesActions.GetCurrentNote)
  async getCurrentNote(ctx: StateContext<PatientDailyNotesStateModel>) {
    const currentNote = await firstValueFrom(this.patientDailyNotesApiService.getCurrentNote());

    ctx.patchState({ currentNote });
  }

  @Action(PatientDailyNotesActions.GetNotesPage)
  async getPage(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.GetNotesPage) {
    const notesPage = await firstValueFrom(this.patientDailyNotesApiService.getPage(action.criteria));

    ctx.patchState({
      dailyNotesForMonth: notesPage.content
    });
  }

  @Action(PatientDailyNotesActions.CreateNote)
  async create(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.CreateNote) {
    const createdNote = await firstValueFrom(this.patientDailyNotesApiService.create(action.payload));

    if (isSameDay(createdNote.date, new Date())) {
      ctx.patchState({ currentNote: createdNote });
    }

    const state = ctx.getState();

    if (state.dailyNotesForMonth) {
      const updatedNotes = [...state.dailyNotesForMonth, createdNote];
      ctx.patchState({ dailyNotesForMonth: updatedNotes });
    }
  }

  @Action(PatientDailyNotesActions.UpdateNote)
  async updateCurrentDailyNote(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.UpdateNote) {
    const updatedNote = await firstValueFrom(this.patientDailyNotesApiService.update(action.payload));
    const state = ctx.getState();

    if (state.currentNote?.id === updatedNote.id) {
      ctx.patchState({ currentNote: updatedNote });
    }

    if (state.dailyNotesForMonth) {
      const updatedNotes = state.dailyNotesForMonth.map(note =>
        note.id === updatedNote.id ? updatedNote : note
      );
      ctx.patchState({ dailyNotesForMonth: updatedNotes });
    }
  }

  @Action(PatientDailyNotesActions.DeleteNote)
  async deleteCurrentDailyNote(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.DeleteNote) {
    const modal = this.modalService.show(ConfirmationModalComponent, {
      class: 'confirmation-modal',
      backdrop: false,
      ignoreBackdropClick: true,
      initialState: {
        title: 'COMPONENT.DAILY_NOTES.DELETE_NOTE_CONFIRMATION.TITLE',
        description: 'COMPONENT.DAILY_NOTES.DELETE_NOTE_CONFIRMATION.DESCRIPTION'
      }
    });

    try {
      const confirmed = await firstValueFrom(modal.content.close$);

      if (!confirmed) {
        return;
      }

      const state = ctx.getState();

      await firstValueFrom(this.patientDailyNotesApiService.delete(action.noteId));

      if (state.currentNote?.id === action.noteId) {
        ctx.patchState({ currentNote: null });
      }

      if (state.dailyNotesForMonth) {
        const updatedNotes = state.dailyNotesForMonth.filter(note =>
          note.id !== action.noteId
        );
        ctx.patchState({ dailyNotesForMonth: updatedNotes });
      }
    } catch (error) {
      console.error('Error in delete operation:', error);
    }
  }

  @Action(PatientDailyNotesActions.GetNoteById)
  async getNoteById(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.GetNoteById) {
    const selectedNote = await firstValueFrom(this.patientDailyNotesApiService.getPatientNoteById(action.noteId));
    
    ctx.patchState({ selectedNote });
  }

  @Action(PatientDailyNotesActions.GetCalendarDays)
  async getCalendarDays(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.GetCalendarDays) {
    const calendarDays = await firstValueFrom(this.patientDailyNotesApiService.getCalendarDays(action.request));
    
    ctx.patchState({ calendarDays });
  }

  @Action(PatientDailyNotesActions.ClearState)
  async clearState(ctx: StateContext<PatientDailyNotesStateModel>, action: PatientDailyNotesActions.ClearState) {
    ctx.patchState({
      currentNote: undefined,
      dailyNotesForMonth: undefined,
      selectedNote: undefined,
      calendarDays: undefined
    });
  }
} 